import {first, isUndefined} from 'lodash';
import React, {FC, useCallback, useRef} from 'react';

import {isDefined} from '../../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {ResourceMap} from '../../../../../../libs/shared/core/src/store/commonAppTypes';
import {CellsConversationsContent} from '../../../../../../srcCommon/inboxes/cells/cellsConversationsSelectors';
import {CellsConversationsDragItem} from '../../../../../helpers/conversationCellHelpers';
import {filterAiMarkForConversation} from '../../../../../helpers/models/conversationAiHelpers';
import {
  ConversationAiResourcesEnum,
  ConversationAiStatusesEnum,
} from '../../../../../models/api/conversationAiMarkModel';
import {ConversationHighlightModel} from '../../../../../models/api/conversationHighlightModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {
  InboxesConversationsParameters,
  InboxesParametersActivity,
} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {
  computeIsIdSelected,
  computeIsMultipleIdsSelected,
  findOnlySelectedId,
  InboxesSelection,
  normalInboxesSelection,
  setInboxesParametersSelection,
} from '../../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {ReadStatusInfoBubble} from '../../../../infoBubbles/readStatusInfoBubble';
import {useCompactP2Context} from '../../compactP2Context';
import {ConversationCellClickEventHandler} from '../../conversationCell';
import {ConversationCellContainer} from '../../conversationCellContainer';
import {CellsConversationsChildrenProps} from '../cellsConversations';
import {CellsItems} from '../cellsItemsBase';

/*
 * Props.
 */

interface CellsConversationsConversationsProps extends CellsConversationsChildrenProps {
  user: TeammateModel;
  content: CellsConversationsContent;
  isReviewingAiTags: boolean;
  selectedDragItem: CellsConversationsDragItem;
}

/*
 * Component.
 */

export const CellsConversationsConversations: FC<CellsConversationsConversationsProps> = (props) => {
  const {page, content, buildParametersPath, onParametersChange, onDoubleClick, user, isReviewingAiTags} =
    props;
  const {parameters, conversationHighlights} = page;

  /*
   * Callbacks.
   */

  const buildSelectionParameters = useCallback(
    (selection: InboxesSelection<number>): InboxesConversationsParameters =>
      setInboxesParametersSelection(parameters, {
        selection,
        activity: isReviewingAiTags
          ? findAiReviewActivityForSelection(content.conversations, selection)
          : findSearchActivityForConversationSelection(conversationHighlights, selection),
      }),
    [content.conversations, conversationHighlights, isReviewingAiTags, parameters],
  );

  const buildConversationPath = useCallback(
    (conversationId: number): string | undefined =>
      buildParametersPath
        ? buildParametersPath(buildSelectionParameters(normalInboxesSelection([conversationId])))
        : undefined,
    [buildSelectionParameters, buildParametersPath],
  );

  const onSelectionChange: (arg: InboxesSelection<number>) => void = useCallback(
    (selection) => onParametersChange(buildSelectionParameters(selection)),
    [buildSelectionParameters, onParametersChange],
  );

  /*
   * Refs.
   */

  const firstCellRef = useRef<HTMLDivElement>(null);

  // We keep track of the two most recent clicks' conversation IDs. This is because we don't
  // want to register a double click event unless both clicks are from the same conversation ID.
  const mostRecentClickConversationIdRef = useRef(-1);
  const secondMostRecentClickConversationIdRef = useRef(-1);

  /*
   * Scroller.
   */

  const {panelId} = props;
  const {cellsContext} = page;
  const {selection} = parameters;
  const {
    conversations,
    conversationIds,
    conversationIndexes,
    extraConversationIds,
    hiddenConversationId,
    conversationOverlays,
  } = content;

  const focusedId = findOnlySelectedId(selection);
  const areHoverActionsEnabled = !computeIsMultipleIdsSelected(selection);

  const renderItem = useCallback(
    (id: number, onClick: ConversationCellClickEventHandler) => {
      const index = conversationIndexes[id];
      const conversation = conversations[index];
      const isSelected = computeIsIdSelected(selection, conversation.id);
      const maybeInnerRef = (index === 0 && firstCellRef) || undefined;

      // Note: We don't set a key prop here because we want to reuse components for efficiency.
      return (
        <ConversationCellContainer
          panelId={panelId}
          conversation={conversation}
          conversationHighlight={conversationHighlights[conversation.id]}
          cellsContext={cellsContext}
          parameters={parameters}
          isExtraConversation={extraConversationIds.has(conversation.id)}
          isSelected={isSelected}
          customDragItem={isSelected ? props.selectedDragItem : undefined}
          conversationOverlay={conversationOverlays[conversation.id]}
          path={buildConversationPath(id)}
          onClick={onClick}
          onDoubleClick={onDoubleClick}
          innerRef={maybeInnerRef}
          mostRecentClickConversationIdRef={mostRecentClickConversationIdRef}
          secondMostRecentClickConversationIdRef={secondMostRecentClickConversationIdRef}
          areHoverActionsEnabled={areHoverActionsEnabled}
          isFirst={index === 0}
          isLast={index === conversations.length - 1}
        />
      );
    },
    [
      conversationIndexes,
      conversations,
      selection,
      panelId,
      conversationHighlights,
      cellsContext,
      extraConversationIds,
      conversationOverlays,
      parameters,
      buildConversationPath,
      onDoubleClick,
      areHoverActionsEnabled,
      props.selectedDragItem,
    ],
  );

  /*
   * Render.
   */

  const {isCompact} = useCompactP2Context();

  return (
    <CellsItems
      userId={user.id}
      panelId={props.panelId}
      hasNextPage={props.hasNextPage}
      page={page}
      reselectDirection={props.reselectDirection}
      itemIds={conversationIds}
      itemIndexes={conversationIndexes}
      renderItem={renderItem}
      defaultId={0}
      focusedId={focusedId}
      hiddenConversationId={hiddenConversationId}
      onSelectionChange={onSelectionChange}
      onRetryClick={props.onRetryClick}
      onRequestMoreAsync={props.onRequestMoreAsync}
      isCompact={isCompact}
      isOffline={props.isOffline}
    >
      {(children, options) => (
        <>
          {props.children(children, options)}
          {firstCellRef.current && <ReadStatusInfoBubble anchor={firstCellRef} />}
        </>
      )}
    </CellsItems>
  );
};

/*
 * Helpers.
 */

function findAiReviewActivityForSelection(
  conversations: ReadonlyArray<ConversationModel>,
  selection: InboxesSelection<number>,
) {
  // If we have more than one selected ID, nothing to do.
  const conversationId = findOnlySelectedId(selection);
  if (isUndefined(conversationId)) {
    return undefined;
  }

  const conversation = conversations.find((c) => c.id === conversationId);

  if (conversation) {
    const suggestedTagMark = first(
      filterAiMarkForConversation(
        conversation,
        ConversationAiResourcesEnum.TAG,
        ConversationAiStatusesEnum.SUGGESTED,
      ),
    );

    if (isDefined(suggestedTagMark)) {
      return {id: suggestedTagMark.activityId};
    }
  }

  return undefined;
}

function findSearchActivityForConversationSelection(
  conversationHighlights: ResourceMap<ConversationHighlightModel>,
  selection: InboxesSelection<number>,
): InboxesParametersActivity | undefined {
  // If we have more than one selected ID, nothing to do.
  const conversationId = findOnlySelectedId(selection);
  if (isUndefined(conversationId)) {
    return undefined;
  }

  // Otherwise, check if we have an activity ID to focus from the search highlight.
  const highlight = conversationHighlights[conversationId];
  const searchActivityId = highlight?.searchActivity?.id;
  if (isUndefined(searchActivityId)) {
    return undefined;
  }

  return {id: searchActivityId};
}
